import { useContext } from '@berlin-front/common/composables/context';

import { computed, toRefs, reactive, unref, watch, Ref, ref } from '@vue/composition-api';
import { useMutation, useQuery } from 'vue-query';
import { Route } from 'vue-router';

import {
  AtoneMemberRegisterPostRequestMemberRegistrationRouteEnum,
  AtoneMemberRegisterPostRequestSexDivisionEnum,
  AtoneMemberRegisterPostResponseSexDivisionEnum,
  AtoneTracksPostRequestAuthenticationTypeEnum,
  GuestTypePostResponseMemberTypeEnum,
  GuestUpdatePostRequestSexDivisionEnum,
  GuestUpdatePostResponseSexDivisionEnum,
} from '@netprotections/berlin-bff';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import useFingerprint from '@berlin-front/common/composables/useFingerprint';
import useOrAll from '@berlin-front/common/composables/useOrAll';
import useFakeProgress from '@berlin-front/common/composables/useFakeProgress';
import { useSessionId } from '@berlin-front/common/composables/useSessionId';
import { ModalBridgeContext } from '@services/ModalBridgeContext';
import { event, set } from 'vue-gtag';
import { BerlinBffContext } from '@/services/BerlinBff';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { MemberRequestContext } from '@/views/member/MemberRequestContext';
import { getRefDeep, getRequiredRefValueByPath, notNil, ValueTypeOf } from '@berlin-front/common/utils';
import useErrors from '@/composables/useErrors';
import { ROUTE_NAME } from '@/router/route-name';
import type { UseSmsAuthContextType } from '@views/auth/sms/context';
import useIsLegacy from '@composables/useIsLegacy';
import { SERVICE_TYPE } from '@services/ModalParameterContext';
import { ATONE_SMS_DESCRIPTION_MESSAGE, AUTHCODE_RESEND_MESSAGE } from './context';

type AuthPageOptions = {
  /**
   * 次ページのRouteName
   */
  nextPage: ValueTypeOf<typeof ROUTE_NAME>;
  /**
   * ユーザ登録画面のRouteName
   */
  registerPage: ValueTypeOf<typeof ROUTE_NAME>;
};

export const useRegisterSmsAuthWith =
  (props: AuthPageOptions) =>
  (_$route: Route): UseSmsAuthContextType => ({
    useSmsAuth: (authCodeRef: Ref) => {
      const [isLegacy] = useIsLegacy();
      const sessionId = useSessionId();
      const router = useRouter();
      const {
        atoneModalParameter,
        tokens,
        atoneRequestTokenPostResult,
        setAtoneMemberRegisterPostResult,
        setAtoneTracksPostResult,
        setRegisterMemberName,
        setAtoneTracksTrustResult,
        setAtonePointGetResult,
        guestTypePostResult,
        setGuestUpdatePostResult,
        setNpTokenAndSignature,
        setPaymentMethodGetResult,
      } = useContext(ApiCacheContext);
      const globals = useContext(ModalBridgeContext);

      const bridge = useContext(ModalBridgeContext);

      const { point, payment, tracks, member, napoleon } = useContext(BerlinBffContext);
      const { registerMemberFormState, isEmailEditable, userNo } = useContext(MemberRequestContext);
      const fingerprint = useFingerprint();
      const { errors, clearAll: clearErrorMessages } = useErrors({
        fieldMapper: {},
        defaultFieldName: 'flashMessages',
      });

      const errorMessages = computed(() => unref(errors)?.flashMessages ?? []);
      const infoMessages = ref<string[]>([]);
      const MEMBER_TYPE = {
        NEW: GuestTypePostResponseMemberTypeEnum.NUMBER_0,
        NP_MEMBER: GuestTypePostResponseMemberTypeEnum.NUMBER_1,
        MEMBER: GuestTypePostResponseMemberTypeEnum.NUMBER_2,
        GUEST: GuestTypePostResponseMemberTypeEnum.NUMBER_3,
      } as const;

      const state = reactive({
        smsToken: '',
      });

      const memberType = computed(() => guestTypePostResult.value?.memberType);

      /**
       * 新規登録ルート
       */
      const MEMBER_REGISTRATION_ROUTE_SMDL_SETTLEMENT_NEW =
        AtoneMemberRegisterPostRequestMemberRegistrationRouteEnum.NUMBER_7;
      /**
       * NPからの会員登録ルート
       */
      const MEMBER_REGISTRATION_ROUTE_SMDL_SETTLEMENT_NP_ID =
        AtoneMemberRegisterPostRequestMemberRegistrationRouteEnum.NUMBER_8;

      // 表示項目
      const phoneNumber = computed(() => registerMemberFormState.phoneNumber);

      const {
        isSuccess: isSuccessGuestUpdate,
        isError: isErrorGuestUpdate,
        mutate: mutateGuestUpdate,
        reset: resetGuestUpdate,
      } = useMutation(
        'guest.metamorphose',
        () =>
          napoleon.guestControllerUpdatePost(getRefDeep(tokens, 'requestToken'), bridge.pubkey ?? '', {
            mailAddress: registerMemberFormState.mailAddress,
            familyName: registerMemberFormState.familyName,
            familyNameKana: registerMemberFormState.familyNameKana,
            givenName: registerMemberFormState.givenName,
            givenNameKana: registerMemberFormState.givenNameKana,
            sexDivision: registerMemberFormState.sexDivision as GuestUpdatePostRequestSexDivisionEnum,
            birthday: [
              registerMemberFormState.birthdayYear,
              registerMemberFormState.birthdayMonth,
              registerMemberFormState.birthdayDay.padStart(2, '0'),
            ].join('-'),
            zipF3: registerMemberFormState.zip.slice(0, 3),
            zipB4: registerMemberFormState.zip.slice(3),
            prefectureCode: registerMemberFormState.prefectureCode,
            address2: [registerMemberFormState.address2First, registerMemberFormState.address2Second].join(''),
            address3: registerMemberFormState.address3,
            phoneNumber: registerMemberFormState.phoneNumber,
            password: registerMemberFormState.password,
            sessionId,
            userNo,
            smsToken: state.smsToken,
            isOldParameter: isLegacy.value,
            requestId: globals.requestId,
          }),
        {
          onSuccess: ({ data }) => {
            setGuestUpdatePostResult(data);
            if (data.authenticationToken) {
              setNpTokenAndSignature(data.authenticationToken, data.signature);
            }
            window.newrelic.addPageAction('complete_member_register', {
              member_id: notNil(data.memberCode),
              shop_id: notNil(data.shopId),
              shop_name: notNil(data.shopName),
              gaid: notNil(data.hashId),
            });

            let gender: string | null;
            // eslint-disable-next-line no-underscore-dangle
            if (data.sexDivision === GuestUpdatePostResponseSexDivisionEnum._1) {
              gender = '男性';
              // eslint-disable-next-line no-underscore-dangle
            } else if (data.sexDivision === GuestUpdatePostResponseSexDivisionEnum._2) {
              gender = '女性';
            } else {
              gender = '不明';
            }

            set({
              user_id: notNil(data.memberCode),
              user_properties: {
                member_id: notNil(data.memberCode),
                gender,
                age: data.age,
                shop_id: notNil(data.shopId),
                // shop_name: notNil(data.shopName),
                guest: 'no',
              },
            });

            event('sign_up', {
              method: 'GUEST ID',
            });
          },
        },
      );

      // SMS検証。
      const {
        isSuccess: isSuccessSmsVerify,
        isError: isErrorSmsVerify,
        mutate: mutateSmsVerify,
        reset: resetSmsVerify,
      } = useMutation(
        'member.SmsVerifyPost',
        () =>
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          member.atoneMemberControllerMemberSmsVerifyPost(getRefDeep(tokens, 'requestToken'), {
            phoneNumber: registerMemberFormState.phoneNumber,
            smsToken: state.smsToken,
          }),
        {
          retry: false,
        },
      );

      // 6桁入力が揃ったタイミングでキックする関数
      const mutate = () => {
        if (memberType.value === MEMBER_TYPE.GUEST) {
          mutateGuestUpdate();
        } else {
          mutateSmsVerify();
        }
      };

      // 会員登録。SMS verifyが成功したら呼び出し
      const {
        isSuccess: isSuccessMemberRegister,
        isError: isErrorMemberRegister,
        mutate: mutateMemberRegister,
      } = useMutation(
        'atone.MemberRegisterPost',
        () =>
          member.atoneMemberControllerMemberRegisterPost(getRefDeep(atoneRequestTokenPostResult, 'requestToken'), {
            shopPublicKey: bridge.pubkey,
            memberRegistrationRoute: unref(isEmailEditable)
              ? MEMBER_REGISTRATION_ROUTE_SMDL_SETTLEMENT_NEW
              : MEMBER_REGISTRATION_ROUTE_SMDL_SETTLEMENT_NP_ID,
            mailAddress: registerMemberFormState.mailAddress,
            familyName: registerMemberFormState.familyName,
            familyNameKana: registerMemberFormState.familyNameKana,
            givenName: registerMemberFormState.givenName,
            givenNameKana: registerMemberFormState.givenNameKana,
            sexDivision: registerMemberFormState.sexDivision as AtoneMemberRegisterPostRequestSexDivisionEnum,
            birthday: [
              registerMemberFormState.birthdayYear,
              registerMemberFormState.birthdayMonth,
              registerMemberFormState.birthdayDay.padStart(2, '0'),
            ].join('-'),
            zipF3: registerMemberFormState.zip.slice(0, 3),
            zipB4: registerMemberFormState.zip.slice(3),
            prefectureCode: registerMemberFormState.prefectureCode,
            address2: [registerMemberFormState.address2First, registerMemberFormState.address2Second].join(''),
            address3: registerMemberFormState.address3,
            phoneNumber: registerMemberFormState.phoneNumber,
            password: registerMemberFormState.password,
            userNo,
            sessionId,
            isOldParameter: isLegacy.value,
            requestId: globals.requestId,
          }),
        {
          onSuccess: ({ data: atoneMemberRegisterResult }) => {
            setAtoneMemberRegisterPostResult(atoneMemberRegisterResult);
            if (atoneMemberRegisterResult.authenticationToken) {
              setNpTokenAndSignature(
                atoneMemberRegisterResult.authenticationToken,
                atoneMemberRegisterResult.signature,
              );
            }
            window.newrelic.addPageAction('complete_member_register', {
              member_id: notNil(atoneMemberRegisterResult.memberCode),
              shop_id: notNil(atoneMemberRegisterResult.shopId),
              shop_name: notNil(atoneMemberRegisterResult.shopName),
              gaid: notNil(atoneMemberRegisterResult.hashId),
            });

            let gender: string | null;
            // eslint-disable-next-line no-underscore-dangle
            if (atoneMemberRegisterResult.sexDivision === AtoneMemberRegisterPostResponseSexDivisionEnum._1) {
              gender = '男性';
              // eslint-disable-next-line no-underscore-dangle
            } else if (atoneMemberRegisterResult.sexDivision === AtoneMemberRegisterPostResponseSexDivisionEnum._2) {
              gender = '女性';
            } else {
              gender = '不明';
            }

            set({
              user_id: notNil(atoneMemberRegisterResult.memberCode),
              user_properties: {
                member_id: notNil(atoneMemberRegisterResult.memberCode),
                gender,
                age: notNil(atoneMemberRegisterResult.age),
                shop_id: notNil(atoneMemberRegisterResult.shopId),
                // shop_name: notNil(atoneMemberRegisterResult.shopName),
                guest: 'no',
              },
            });

            event('sign_up', {
              method: unref(isEmailEditable) ? 'New' : 'NP ID',
            });
          },
        },
      );

      // フィンガープリント登録。会員登録後に呼び出し。
      const {
        isSuccess: isSuccessTrackTokenPost,
        isError: isErrorTrackTokenPost,
        mutate: mutateTrackToken,
      } = useMutation(
        'atone.atoneTrackTokenPost',
        () =>
          tracks.atoneTracksControllerTrackTokenPost(getRefDeep(tokens, 'npToken'), notNil(bridge.pubkey), {
            authenticationType: AtoneTracksPostRequestAuthenticationTypeEnum.NUMBER_1,
            fingerPrint: fingerprint.value,
          }),
        {
          onSuccess: ({ data }) => {
            setAtoneTracksPostResult(data);
            setRegisterMemberName(registerMemberFormState.familyName + registerMemberFormState.givenName);
            setAtoneTracksTrustResult({ trustedFlag: true });
          },
        },
      );

      // ポイント取得。フィンガープリント更新に成功したら呼び出し。
      const { isSuccess: isSuccessAtonePointGet, isError: isErrorAtonePointGet } = useQuery(
        'atone.atonePointGet',
        () =>
          point.atonePointControllerPointGet(
            getRefDeep(tokens, 'npToken'),
            notNil(bridge.pubkey),
            getRefDeep(tokens, 'trustedTrackToken'), // FIXME: 「信頼トークン確認」でfalseが帰ってきた場合、undefinedになってバグる
          ),
        {
          enabled: isSuccessTrackTokenPost,
          onSuccess: ({ data: atonePointGetResult }) => {
            setAtonePointGetResult(atonePointGetResult);
          },
        },
      );

      const { isSuccess: isSuccessPaymentMethodGet, isError: isErrorPaymentMethodGet } = useQuery(
        'atone.atonePaymentControllerMethodGet',
        () =>
          payment.atonePaymentControllerMethodGet(
            getRefDeep(tokens, 'npToken'),
            getRefDeep(tokens, 'trustedTrackToken'),
            getRequiredRefValueByPath({ atoneModalParameter }, 'pubKey'),
            getRequiredRefValueByPath({ atoneModalParameter }, 'payment').amount,
            atoneModalParameter.value?.payment?.transactionOptions,
          ),
        {
          enabled: bridge.serviceType === SERVICE_TYPE.ATONE && isSuccessAtonePointGet,
          onSuccess: ({ data: atoneMethodGetResult }) => {
            setPaymentMethodGetResult(atoneMethodGetResult);
          },
        },
      );

      // SMS送信
      const {
        isSuccess: isSuccessSmsNotifyPost,
        isError: isErrorSmsNotifyPost,
        mutate: mutateSmsResend,
        reset: resetSmsResend,
      } = useMutation(
        'member.SmsNotifyPost',
        () =>
          member.atoneMemberControllerMemberSmsNotify(getRefDeep(tokens, 'requestToken'), {
            phoneNumber: registerMemberFormState.phoneNumber,
            mailAddress: registerMemberFormState.mailAddress,
            resend: true,
          }),
        {
          onSuccess: () => {
            infoMessages.value = [AUTHCODE_RESEND_MESSAGE];
          },
        },
      );

      // 依存するAPIの成功に続けて、mutateを実行する
      watch(isSuccessSmsVerify, () => {
        if (unref(isSuccessSmsVerify)) mutateMemberRegister();
      });
      watch([isSuccessMemberRegister, isSuccessGuestUpdate], () => {
        if (unref(isSuccessMemberRegister) || unref(isSuccessGuestUpdate)) mutateTrackToken();
      });

      const isLoading = ref(false);
      const isError = useOrAll(
        isErrorMemberRegister,
        isErrorSmsVerify,
        isErrorGuestUpdate,
        isErrorTrackTokenPost,
        isErrorAtonePointGet,
        isErrorSmsNotifyPost,
        isErrorPaymentMethodGet,
      );
      let isSuccessSmsAuth: Ref<boolean>;
      if (bridge.serviceType === SERVICE_TYPE.ATONE) {
        isSuccessSmsAuth = isSuccessPaymentMethodGet;
      } else {
        isSuccessSmsAuth = isSuccessAtonePointGet;
      }
      const isSuccess = useOrAll(isSuccessSmsNotifyPost, isSuccessSmsAuth);

      // 成功、または失敗になったらisEnded
      const isEnded = useOrAll(isError, isSuccess);

      // 進捗。useMutationの状況に合わせてプログレスを進める。
      const {
        progress,
        completed: progressCompleted,
        isLoading: isLoadingApi,
      } = toRefs(useFakeProgress(5000, isLoading, isEnded));

      const clearInfoMessages = () => {
        infoMessages.value = [];
      };

      // そのままmutateをキックするのではなく、
      // - 実行中でないことを確認
      // - 実行後（正常系）でないことを確認
      // した上でリクエストを行う。（バリデーションはAuthCodeInput内で確認済）
      const onSubmitVerify = (token: string) => {
        if (!unref(isLoadingApi) && !unref(isSuccessSmsAuth)) {
          state.smsToken = token;
          clearErrorMessages();
          clearInfoMessages();
          isLoading.value = true;
          mutate();
        }
      };

      const backToRegister = () => {
        void router.replace({ name: props.registerPage });
      };

      const onSubmitResend = () => {
        if (!unref(isLoadingApi)) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          authCodeRef.value.clearNumbers(); // 認証コード入力フォームをクリア
          clearErrorMessages();
          clearInfoMessages();
          isLoading.value = true;
          mutateSmsResend();
        }
      };

      watch(progressCompleted, (result) => {
        if (result) {
          if (unref(isSuccessSmsAuth) && unref(progressCompleted)) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            router.replace({ name: props.nextPage }); // 認証成功なら画面遷移
            return;
          }
          isLoading.value = false;
          resetSmsResend.value?.();
          resetSmsVerify.value?.();
          resetGuestUpdate.value?.();
        }
      });

      // エラーが発生したら認証コードを消去する
      watch(isError, () => {
        if (isError.value) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          authCodeRef.value.clearNumbers(); // 認証コード入力フォームをクリア
        }
      });

      return reactive({
        // 表示項目
        description: ATONE_SMS_DESCRIPTION_MESSAGE,
        phoneNumber,
        errorMessages,
        infoMessages,

        // progress
        progress,

        // loading
        isLoadingApi,

        // 関数
        backToRegister,
        backToPrevious: undefined,
        onSubmitVerify,
        onSubmitResend,
      });
    },
  });
